import React, { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'

import { Text } from '@cash-me/react-components'

import { TokenFieldGroup, TokenField, TokenErrorMessage } from './styles'

type TokenFieldProps = {
  errorMessage?: string
  length?: number
  onFilledToken: (token: string) => void
  clearErrorMessage: () => void
}

export const TokenFieldComponent = (props: TokenFieldProps) => {
  const INITIAL_VALUE = useCallback(() => new Array(6).fill(''), [])

  const firstField = useRef<HTMLInputElement>(null)
  const [tokenState, setTokenState] = useState<'default' | 'filled' | 'error'>('default')
  const [token, setToken] = useState<string[]>(INITIAL_VALUE())

  const applyFocusToPreviousField = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    const target = event?.target as HTMLInputElement
    const previousField = target?.previousElementSibling as HTMLInputElement
    if (previousField) previousField.focus()
  }, [])

  const applyFocusToNextField = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    const target = event?.target as HTMLInputElement
    const nextField = target?.nextElementSibling as HTMLInputElement
    if (nextField) nextField.focus()
  }, [])

  const onlyNumber = useCallback((value: string) => value?.replace(/[^0-9]/g, ''), [])

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, index: number) => {
      const eventValue = event.target?.value ?? ''
      const sanitizedValue = onlyNumber(eventValue)
      if (/^(\d|\s)*$/.test(eventValue)) {
        // Copy-paste check
        if (sanitizedValue?.length > 2) {
          setToken(sanitizedValue.slice(0, 6).split(''))
        } else {
          setToken(token.map((item, idx) => (index === idx ? sanitizedValue.replace(item, '') : item)))
        }
      }
    },
    [onlyNumber, token]
  )

  const handleKeyUp = useCallback(
    (event: KeyboardEvent<HTMLInputElement>, index: number) => {
      if (/^(\d|Backspace)$/.test(event.key)) {
        if (event.key === 'Backspace') {
          if (token[index] === '' && index > 0) applyFocusToPreviousField(event)
        } else if (index < token.length - 1) applyFocusToNextField(event)
      }
    },
    [applyFocusToNextField, applyFocusToPreviousField, token]
  )

  const changeTokenState = useCallback(() => {
    const isFilled = token.every(value => !!value)
    if (isFilled) {
      setTokenState('filled')
      props.clearErrorMessage()
      return props.onFilledToken(token.join(''))
    } else {
      setTokenState('default')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  useEffect(() => {
    changeTokenState()
  }, [changeTokenState])

  useEffect(() => {
    if (props.errorMessage && typeof props.errorMessage === 'string') setTokenState('error')
  }, [props.errorMessage])

  useEffect(() => {
    firstField.current?.focus()
  }, [])

  return (
    <>
      <TokenFieldGroup>
        {token.map((token, index) => (
          <TokenField
            {...(index === 0 && { ref: firstField })}
            type="text"
            autoComplete="one-time-code"
            key={index}
            value={token}
            inputMode="numeric"
            pattern="[0-9]*"
            onChange={event => handleChange(event, index)}
            onKeyUp={event => handleKeyUp(event, index)}
            tokenState={tokenState}
          />
        ))}
      </TokenFieldGroup>
      {props.errorMessage && typeof props.errorMessage === 'string' && (
        <TokenErrorMessage>
          <Text size="caption" color="var(--colors-danger-text-hover)">
            {props.errorMessage}
          </Text>
        </TokenErrorMessage>
      )}
    </>
  )
}
